import imageCompression from 'browser-image-compression';
import { readAndCompressImage } from 'browser-image-resizer';

export interface ImageProcessOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
  maintainAspectRatio?: boolean;
}

/**
 * 处理图片（调整大小、压缩、转换格式）
 * @param file 原始图片文件
 * @param options 处理选项
 * @returns 处理后的图片文件
 */
export const processImage = async (
  file: File,
  options: ImageProcessOptions
): Promise<File> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'jpeg',
    // 我们在配置中使用maintainAspectRatio
    maintainAspectRatio = true
  } = options;

  // 调整图片大小
  const config = {
    quality,
    maxWidth,
    maxHeight,
    autoRotate: true,
    debug: false,
    mimeType: `image/${format}`,
    maxSize: 10485760, // 10MB
    // 使用maintainAspectRatio参数
    maintainAspectRatio
  };

  try {
    // 首先调整大小
    const resizedBlob = await readAndCompressImage(file, config);
    
    // 如果需要进一步压缩
    if (quality < 0.9) {
      const compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: Math.max(maxWidth, maxHeight),
        useWebWorker: true,
        fileType: `image/${format}`,
      };
      
      const compressedBlob = await imageCompression(
        new File([resizedBlob], file.name, { type: resizedBlob.type }),
        compressionOptions
      );
      
      return new File(
        [compressedBlob], 
        `${file.name.split('.')[0]}.${format}`,
        { type: `image/${format}` }
      );
    }
    
    return new File(
      [resizedBlob], 
      `${file.name.split('.')[0]}.${format}`,
      { type: `image/${format}` }
    );
  } catch (error) {
    console.error('图片处理失败:', error);
    throw new Error('图片处理失败');
  }
};

/**
 * 获取图片的尺寸信息
 * @param file 图片文件
 * @returns 包含宽度和高度的Promise
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      reject(new Error('无法加载图片'));
    };
    img.src = URL.createObjectURL(file);
  });
};

/**
 * 将图片转换为Data URL
 * @param file 图片文件
 * @returns Data URL字符串
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error('无法读取文件'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * 下载处理后的图片
 * @param file 处理后的图片文件
 */
export const downloadImage = (file: File): void => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(file);
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};